if (!customElements.get('lookbook-card-slider')) {
  customElements.define(
    'lookbook-card-slider',
    class LookbookCardSlider extends HTMLElement {
      constructor() {
        super();
      }

      connectedCallback() {
        this.sliderWrapper = this.querySelector('.swiper');
        if (!this.sliderWrapper) {
          return;
        }

        this.lookbookIcons = this.querySelectorAll('lookbook-icon');
        this.productCards = this.querySelectorAll('.product-card');
        this.hintEl = this.querySelector('[data-hotspot-hint]');
        this.sliderInstance = false;
        this._lastActivatedIcon = null;
        this.initSlider();

        this._onMouseOver = this.onMouseOver.bind(this);
        this._onMouseLeave = this.onMouseLeave.bind(this);
        this._onFocus = this.onFocus.bind(this);
        this._onBlur = this.onBlur.bind(this);
        this._onKeydown = this.onKeydown.bind(this);
        this._onSliderKeydown = this.onSliderKeydown.bind(this);

        Array.from(this.lookbookIcons).forEach((icon) => {
          icon.addEventListener('mouseover', this._onMouseOver);
          icon.addEventListener('mouseleave', this._onMouseLeave);
          icon.addEventListener('focus', this._onFocus);
          icon.addEventListener('blur', this._onBlur);
          icon.addEventListener('keydown', this._onKeydown);
        });

        this.sliderArea = this.querySelector('.col-lookbook-slider');
        if (this.sliderArea) {
          this.sliderArea.addEventListener('keydown', this._onSliderKeydown);
        }
      }

      disconnectedCallback() {
        Array.from(this.lookbookIcons).forEach((icon) => {
          icon.removeEventListener('mouseover', this._onMouseOver);
          icon.removeEventListener('mouseleave', this._onMouseLeave);
          icon.removeEventListener('focus', this._onFocus);
          icon.removeEventListener('blur', this._onBlur);
          icon.removeEventListener('keydown', this._onKeydown);
        });

        if (this.sliderArea) {
          this.sliderArea.removeEventListener('keydown', this._onSliderKeydown);
        }
      }

      initSlider() {
        const items = parseInt(this.dataset.items);
        const columnGap = FoxTheme.utils.getGridColumnGap(this.sliderWrapper);

        this.sliderOptions = {
          init: false,
          slidesPerView: 1,
          spaceBetween: columnGap.desktop,
          breakpoints: {
            768: {
              slidesPerView: items,
            },
          },
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            renderBullet: function(index, className) {
              return `<button class="${className}"></button>`;
            },
          },
          speed: 500,
          loop: true,
          threshold: 2,
          on: {
            afterInit: (swiper) => {
              this.waitForSwiperSlides(swiper).then(() => {
                this.onSlideAfterInit(swiper);
              });
            },
            realIndexChange: (swiper) => {
              this.waitForSwiperSlides(swiper).then(() => {
                this.onSlideChange(swiper);
              });
            },
          },
        };

        this.sliderCounter = this.querySelector('.swiper-pagination-counter');
        this.sliderWrapper.querySelector('.swiper-wrapper').classList.remove('f-grid');

        this.sliderInstance = new window.FoxTheme.Carousel(this.sliderWrapper, this.sliderOptions);
        this.sliderInstance.init();
        this.sliderInstance.slider.init();

        // Do not use on event like this when set init: false, use on: {} instead
        // this.sliderInstance.slider.on('afterInit', this.onSlideAfterInit.bind(this));
        // this.sliderInstance.slider.on('realIndexChange', this.onSlideChange.bind(this));

        this.fixQuickviewDuplicate();
        this.calcNavButtonsPosition();

        window.addEventListener(
          'resize',
          FoxTheme.utils.debounce(() => {
            this.calcNavButtonsPosition();
          }, 100)
        );
      }

      // Helper function to wait for swiper.slides to be available
      waitForSwiperSlides(swiper, maxAttempts = 50, interval = 100) {
        return new Promise((resolve, reject) => {
          let attempts = 0;

          const checkSlides = () => {
            if (swiper.slides && swiper.slides.length > 0) {
              resolve(swiper);
            } else if (attempts >= maxAttempts) {
              reject(new Error('Swiper slides not available after maximum attempts'));
            } else {
              attempts++;
              setTimeout(checkSlides, interval);
            }
          };

          checkSlides();
        });
      }

      calcNavButtonsPosition() {
        if (this.dataset.calcButtonPosition !== 'true') return;

        const firstMedia = this.querySelector('.product-card__image-wrapper');
        if (firstMedia && firstMedia.clientHeight > 0) {
          this.style.setProperty('--swiper-navigation-top-offset', parseInt(firstMedia.clientHeight) / 2 + 'px');
        }
      }

      fixQuickviewDuplicate() {
        let modalIds = [];
        Array.from(this.querySelectorAll('quick-view-modal')).forEach((modal) => {
          const modalID = modal.getAttribute('id');
          if (modalIds.includes(modalID)) {
            modal.remove();
          } else {
            modalIds.push(modalID);
          }
        });
      }

      onSlideAfterInit(swiper) {
        const { realIndex } = swiper;
        if (this.sliderCounter && swiper.pagination.bullets) {
          this.sliderCounter.querySelector('.swiper-pagination-current').innerText = realIndex + 1;
          this.sliderCounter.querySelector('.swiper-pagination-total').innerText = swiper.pagination.bullets.length;
        }

        if (swiper.pagination && swiper.pagination.bullets.length) {
          const total = swiper.pagination.bullets.length;
          swiper.pagination.bullets.forEach((bullet, i) => {
            bullet.setAttribute('aria-label', `Go to slide ${i + 1} of ${total}`);
            if (i === swiper.realIndex) bullet.setAttribute('aria-current', 'true');
          });
        }

        this.detectVisibleSlides(swiper);
      }

      onSlideChange(swiper) {
        const { slides, realIndex } = swiper;

        this.detectVisibleSlides(swiper);

        if (slides[realIndex]) {
          Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-active'));
          this.lookbookIcons[realIndex] && this.lookbookIcons[realIndex].classList.add('is-active');
        }

        if (this.sliderCounter && swiper.pagination.bullets) {
          this.sliderCounter.querySelector('.swiper-pagination-current').innerText = realIndex + 1;
        }

        if (swiper.pagination && swiper.pagination.bullets.length) {
          swiper.pagination.bullets.forEach((bullet, i) => {
            if (i === realIndex) {
              bullet.setAttribute('aria-current', 'true');
            } else {
              bullet.removeAttribute('aria-current');
            }
          });
        }
      }

      detectVisibleSlides(swiper) {
        // Check if swiper.slides is available before proceeding
        if (!swiper.slides || swiper.slides.length === 0) {
          console.warn('Swiper slides not available yet');
          return;
        }

        const {
          activeIndex,
          params: { slidesPerView },
        } = swiper;

        this.activeIndexs = [];
        for (let i = 0; i < slidesPerView; i++) {
          const slideIndex = (activeIndex + i) % swiper.slides.length;
          const index = Number(swiper.slides[slideIndex].dataset.index);
          this.activeIndexs.push(index);
        }

        this.animateVisiblePins();
      }

      animateVisiblePins() {
        this.resetAnimatePins();
        this.startAnimatePins();
      }

      resetAnimatePins() {
        Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-visible'));
      }

      startAnimatePins() {
        Array.from(this.activeIndexs).forEach((index) => {
          this.lookbookIcons[index] && this.lookbookIcons[index].classList.add('is-visible');
        });
      }

      activateHotspot(icon, moveFocus) {
        const index = Number(icon.dataset.index);
        icon.classList.add('is-active');

        if (typeof this.sliderInstance.slider === 'object') {
          if (this.sliderOptions.loop) {
            this.sliderInstance.slider.slideToLoop(index, this.sliderOptions.speed);
          } else {
            this.sliderInstance.slider.slideTo(index, this.sliderOptions.speed);
          }

          this.classList.add('is-hovering');
          this.resetAnimatePins();

          if (moveFocus) {
            this._lastActivatedIcon = icon;
            const slide = this.querySelector(`.swiper-slide[data-index="${index}"]`);
            if (slide) {
              const focusTarget = slide.querySelector('a, button, [tabindex="0"]');
              if (focusTarget) {
                setTimeout(() => {
                  if (this.hintEl) {
                    focusTarget.setAttribute('aria-describedby', this.hintEl.id);
                  }
                  focusTarget.focus();
                  this._focusedSlideTarget = focusTarget;
                }, this.sliderOptions.speed);
              }
            }
          }
        }
      }

      clearSlideHint() {
        if (this._focusedSlideTarget) {
          this._focusedSlideTarget.removeAttribute('aria-describedby');
          this._focusedSlideTarget = null;
        }
      }

      deactivateHotspots() {
        Array.from(this.productCards).forEach((card) => card.classList.remove('is-active'));
        Array.from(this.lookbookIcons).forEach((icon) => icon.classList.remove('is-active'));
        this.classList.remove('is-hovering');

        this.startAnimatePins();
      }

      onMouseOver(event) {
        this.activateHotspot(event.currentTarget);
      }

      onMouseLeave(event) {
        this.deactivateHotspots();
      }

      onFocus(event) {
        event.currentTarget.classList.add('is-active');
      }

      onBlur(event) {
        event.currentTarget.classList.remove('is-active');
      }

      onKeydown(event) {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          this.activateHotspot(event.currentTarget, true);
        }
      }

      onSliderKeydown(event) {
        if (event.key === 'Escape' && this._lastActivatedIcon) {
          event.preventDefault();
          this.clearSlideHint();
          this._lastActivatedIcon.focus();
          this._lastActivatedIcon = null;
          this.deactivateHotspots();
        }
      }
    }
  );
}
